import React, { FC, useEffect, useRef, useState } from 'react';
import { Button, useTheme } from '@mui/material';
import toast from 'react-hot-toast';
import axiosInstant from '../../api';
import { SingleSubSubProfessionEndpoint, adminSubSubProfessionAttributeValues } from '../../api/endpoints';
import { DotLottleLoader } from '../../components/Loader';
import {
  ActionIconButton,
  FlashWrapper,
  InlineInput,
  SubLine,
} from '../professions/constants';
import { ConfirmationDialog } from '../../components/Modal/ConfirmationDialog';
import { IconTrash, IconPencil, IconConfirm, IconClose, IconSettings } from '../../utils/icons';
import { AttributeOptionsModal } from '../../components/AttributeOptions/AttributeOptionsModal';

export interface IAreaOfFocus {
  id: number;
  name: string;
  professionId?: number;
}

interface Props {
  areaOfFocus: IAreaOfFocus;
  parentColor?: string;
  onUpdate(areaOfFocusId: number, newItem: IAreaOfFocus | null): void;
  isNew?: boolean;
}

export const AreaOfFocusListItem: FC<Props> = ({
  areaOfFocus,
  parentColor = '#D5D0C8',
  onUpdate,
  isNew,
}) => {
  const theme = useTheme();
  const inputRef = useRef<HTMLInputElement>(null);
  const [editing, setEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [attrsOpen, setAttrsOpen] = useState(false);
  const [newName, setNewName] = useState(areaOfFocus.name);
  const [flash, setFlash] = useState<'' | 'green' | 'red'>('');

  useEffect(() => {
    if (isNew) {
      setFlash('green');
      setTimeout(() => setFlash(''), theme.transitions.duration.standard * 2);
    }
  }, []);

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [editing]);

  const handleDelete = async () => {
    setLoading(true);
    try {
      await axiosInstant.delete(SingleSubSubProfessionEndpoint(areaOfFocus.id));
      setDeleting(false);
      toast.success(`Area of focus '${areaOfFocus.name}' removed!`);
      setTimeout(() => onUpdate(areaOfFocus.id, null), 100);
    } catch (error) {
      console.error(error);
      toast.error('Failed to delete');
      setFlash('red');
    }
  };

  const handleNameSave = async () => {
    setLoading(true);
    try {
      const response = await axiosInstant.patch(SingleSubSubProfessionEndpoint(areaOfFocus.id), { name: newName });
      onUpdate(areaOfFocus.id, response.data);
      toast.success('Area of focus successfully updated!');
      setEditing(false);
      setFlash('green');
    } catch (error) {
      console.error(error);
      toast.error('Failed to update');
      setFlash('red');
    }
    setTimeout(() => setFlash(''), theme.transitions.duration.standard);
    setLoading(false);
  };

  const handleNameCancel = () => {
    setEditing(false);
    setNewName(areaOfFocus.name);
  };

  return (
    <>
      <FlashWrapper flash={flash}>
        <div className="flex items-center gap-2.5 pl-7 pr-4 py-2 sm:pl-4 sm:pr-3 sm:py-1.5">
          <SubLine color={parentColor} />

          {editing ? (
            <InlineInput
              ref={inputRef}
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleNameSave();
                if (e.key === 'Escape') handleNameCancel();
              }}
            />
          ) : (
            <span className="flex-1 min-w-0 truncate text-sm text-text-1">{areaOfFocus.name}</span>
          )}

          <div className="flex items-center gap-0.5 ml-auto shrink-0">
            {!loading && !editing && (
              <>
                <ActionIconButton title="Attribute options" onClick={() => setAttrsOpen(true)}>
                  <IconSettings width={14} height={14} stroke="currentColor" />
                </ActionIconButton>
                <ActionIconButton title="Edit" onClick={() => setEditing(true)}>
                  <IconPencil size={13} stroke="currentColor" />
                </ActionIconButton>
                <ActionIconButton title="Delete" onClick={() => setDeleting(true)}>
                  <IconTrash width={13} height={13} stroke="currentColor" />
                </ActionIconButton>
              </>
            )}
            {!loading && editing && (
              <>
                <ActionIconButton tone="confirm" title="Save" onClick={handleNameSave}>
                  <IconConfirm size={13} />
                </ActionIconButton>
                <ActionIconButton title="Cancel" onClick={handleNameCancel}>
                  <IconClose size={13} />
                </ActionIconButton>
              </>
            )}
            {loading && <DotLottleLoader sx={{ width: 28, height: 28 }} fill={false} />}
          </div>
        </div>
      </FlashWrapper>

      <ConfirmationDialog
        title="Delete Area of Focus"
        description={`Are you sure you want to delete '${areaOfFocus.name}'? This action cannot be undone.`}
        show={deleting}
        onConfirm={handleDelete}
        onCancel={() => setDeleting(false)}
        confirmButton={({ onClick }) => (
          <Button color="red" startIcon={<IconTrash width={24} height={24} stroke={theme.palette.common.white} />} onClick={onClick}>
            Delete Area of Focus
          </Button>
        )}
      />

      {attrsOpen && (
        <AttributeOptionsModal
          title={`Attribute options — ${areaOfFocus.name}`}
          endpoint={adminSubSubProfessionAttributeValues(areaOfFocus.id)}
          onClose={() => setAttrsOpen(false)}
        />
      )}
    </>
  );
};
